import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TeamsMeetingLinkLocator } from '@azure/communication-calling';
import { CommunicationUserIdentifier } from '@azure/communication-common';
import {
    toFlatCommunicationIdentifier,
    useAzureCommunicationCallWithChatAdapter,
    CallAndChatLocator,
    CallWithChatAdapterState,
    CallWithChatComposite,
    CallWithChatAdapter,
    CallWithChatCompositeOptions,
    COMPOSITE_LOCALE_PT_BR,
    COMPOSITE_LOCALE_ES_ES,
    COMPOSITE_LOCALE_EN_US,
    CallAdapterCallEndedEvent,
} from '@azure/communication-react';
import { Loader } from "@fluentui/react-northstar";
import { useSwitchableFluentTheme } from '../theming/SwitchableFluentThemeProvider';
import { createAutoRefreshingCredential } from '../utils/credential';
import { useIsMobile } from '../utils/useIsMobile';
import { withTranslation, WithTranslation } from "react-i18next";

const _ = require('lodash');

export interface CallScreenProps extends WithTranslation {
    token: string;
    userId: CommunicationUserIdentifier;
    displayName: string;
    endpoint: string;
    locator: CallAndChatLocator | TeamsMeetingLinkLocator;
    callStarted: Function;
    callEnded: Function;
    queueData: any;
}

const CallScreen = (props: CallScreenProps): JSX.Element => {
    const localize = props.t;
    const {
        token,
        userId,
        displayName,
        endpoint,
        locator,
    } = props;
    const callIdRef = useRef<string>();
    const { currentTheme, currentRtl } = useSwitchableFluentTheme();
    const isMobileSession = useIsMobile();

    const credential = useMemo(
        () => createAutoRefreshingCredential(toFlatCommunicationIdentifier(userId), token),
        [userId, token]
    );

    const afterAdapterCreate = useCallback(
        async (adapter: CallWithChatAdapter): Promise<CallWithChatAdapter> => {
            adapter.on('callError', (e) => console.log('Adapter error event:', e));

            adapter.on('chatError', (e) => console.log('Adapter error event:', e));

            adapter.on('callEnded', (e: CallAdapterCallEndedEvent) => {
                var adapterState = adapter.getState();
                console.log("callEnded")

                props.callEnded()  
            });

            adapter.on('callParticipantsLeft', (e) => {
                var adapterState = adapter.getState();

                //if (_.isEmpty(adapterState?.call?.remoteParticipants) &&
                //   (_.isEmpty(adapterState?.call?.breakoutRooms) || !_.isEmpty(adapterState?.call?.breakoutRooms) && adapterState?.call?.breakoutRooms?.assignedBreakoutRoom?.state === "closed")) {
                //    adapter.leaveCall();
                //    props.callEnded();
                //}

                console.log("callParticipantsLeft")

                if (_.isEmpty(adapterState?.call?.remoteParticipants) &&
                    (_.isEmpty(adapterState?.call?.breakoutRooms))) {
                    adapter.leaveCall();
                    props.callEnded();
                }
            });

            adapter.on('breakoutRoomsUpdated', (e) => {
                if (e.type === 'assignedBreakoutRooms') {
                    console.log('Assigned breakout rooms event: ', e);
                } else if (e.type === 'join') {
                    console.log('Breakout rooms join event: ', e);
                } else if (e.type === 'breakoutRoomsSettings') {
                    console.log('Breakout rooms settings event: ', e);
                }
            });

            adapter.onStateChange(async (state: CallWithChatAdapterState) => {
                const currentState = convertPageStateToString(state);

                if (currentState === "call" && state?.call?.state !== "Disconnecting") {
                    props.callStarted();
                }

                if (state?.call?.id && callIdRef.current !== state?.call?.id) {
                    callIdRef.current = state?.call?.id;
                    console.log(`Call Id: ${callIdRef.current}`);
                }
            });

            return adapter;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [callIdRef]
    );

    const adapter = useAzureCommunicationCallWithChatAdapter(
        {
            userId,
            displayName,
            credential,
            endpoint,
            locator,
        },
        afterAdapterCreate
    );

    // Dispose of the adapter in the window's before unload event.
    // This ensures the service knows the user intentionally left the call if the user
    // closed the browser tab during an active call.
    useEffect(() => {
        const disposeAdapter = (): void => adapter?.dispose();
        window.addEventListener('beforeunload', disposeAdapter);
        return () => window.removeEventListener('beforeunload', disposeAdapter);
    }, [adapter]);

    if (!adapter) {
        return (
            <div className="loader">
                <Loader size="small" label={localize("Carregando")} />
            </div>
        );
    }

    const compositeOptions: CallWithChatCompositeOptions = {
        callControls: {
            chatButton: true,
            peopleButton: true,
            screenShareButton: false,
            displayType: isMobileSession ? "compact" : "default",
            raiseHandButton: false,
            moreButton: false,
            dtmfDialerButton: false,
            endCallButton: {
                disableEndCallModal: true,
            }
        },
        surveyOptions: {
            disableSurvey: true,
        },
    }

    const _adapter: any = Object.assign({}, adapter);

    return (
        <div className="callCompositeRoot">
            <CallWithChatComposite
                adapter={adapter}
                fluentTheme={currentTheme.theme}
                rtl={currentRtl}
                joinInvitationURL={window.location.href}
                formFactor={isMobileSession ? 'mobile' : 'desktop'}
                locale={
                    props.i18n.language.includes("pt") ? COMPOSITE_LOCALE_PT_BR :
                        props.i18n.language.includes("es") ? COMPOSITE_LOCALE_ES_ES :
                            COMPOSITE_LOCALE_EN_US
                }
                options={compositeOptions}
            />
        </div>
    );
};

const callScreenWithTranslation = withTranslation()(CallScreen);
export default callScreenWithTranslation;

const convertPageStateToString = (state: CallWithChatAdapterState): string => {
    switch (state.page) {
        case 'accessDeniedTeamsMeeting':
            return 'error';
        case 'leftCall':
            return 'end call';
        case 'removedFromCall':
            return 'end call';
        default:
            return `${state.page}`;
    }
};
